package songwritersnotebook.app.data

import songwritersnotebook.database.AppDatabase
import songwritersnotebook.database.RhymeWordEntity
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.coroutines.sync.withLock
import songwritersnotebookapp.composeapp.generated.resources.Res
import org.jetbrains.compose.resources.ExperimentalResourceApi
import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOne
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull

data class RhymeWord(
    val word: String,
    val phonemes: String
)

object RhymeRepository {
    private val scope = CoroutineScope(Dispatchers.Default)
    private val _isInitialized = MutableStateFlow(false)
    val isInitialized = _isInitialized.asStateFlow()
    private var isInitializing = false

    @OptIn(ExperimentalResourceApi::class)
    fun initialize(database: AppDatabase) {
        if (_isInitialized.value || isInitializing) return
        
        val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
        if (isWeb) {
            println("RhymeRepository: Web target detected. Using Firebase-backed dictionary (Lazy load).")
            _isInitialized.value = true
            return
        }

        isInitializing = true
        scope.launch {
            val queries = database.appDatabaseQueries
            try {
                // Check if we need to import or re-import for multi-pronunciation support
                val existingCount = try {
                    // Check if "DESERT" has multiple definitions (new schema support)
                    val desertDefs = queries.getWordDefinition("DESERT").awaitAsList()
                    if (desertDefs.size < 2) {
                        println("RhymeRepository: Multi-pronunciation support (e.g. DESERT) missing. Re-importing.")
                        0L
                    } else {
                        queries.getRhymeWordCount().awaitAsOne()
                    }
                } catch (e: Exception) {
                    println("RhymeRepository: Schema mismatch or missing table. Clearing for re-import: $e")
                    0L
                }

                if (existingCount > 100000L) { // Full dictionary is ~134k
                    println("RhymeRepository: Dictionary already populated ($existingCount words).")
                    _isInitialized.value = true
                    return@launch
                }

                println("RhymeRepository: Starting dictionary import...")
                val startTime = ClockTime.now()
                
                // Clear any partial/old data
                try { queries.clearRhymeWords() } catch (e: Exception) {}

                val bytes = Res.readBytes("files/cmudict.txt")
                val text = bytes.decodeToString()
                val allLines = text.split('\n')
                
                // Process in larger chunks for speed, but use yield to keep UI alive
                allLines.chunked(2000).forEach { chunk ->
                    database.transaction {
                        chunk.forEach { line ->
                            if (!line.startsWith(";;;") && line.isNotBlank()) {
                                val cleanLine = line.trim()
                                // Faster split than Regex
                                val firstSpace = cleanLine.indexOf(' ')
                                if (firstSpace != -1) {
                                    var word = cleanLine.substring(0, firstSpace).trim()
                                    val phonemes = cleanLine.substring(firstSpace).trim()
                                    if (word.isNotEmpty() && phonemes.isNotEmpty()) {
                                        // Handle words with multiple pronunciations like DESERT(1), DESERT(2)
                                        if (word.contains('(')) {
                                            word = word.substringBefore('(').trim()
                                        }
                                        val stressedSuffix = getStressedSuffix(phonemes)
                                        queries.insertRhymeWord(word, phonemes, stressedSuffix)
                                    }
                                }
                            }
                        }
                    }
                    kotlinx.coroutines.yield() 
                }
                println("RhymeRepository: Import finished successfully in ${ClockTime.now() - startTime}ms")
                _isInitialized.value = true
            } catch (e: Exception) {
                println("RhymeRepository: Error importing dictionary: ${e.message}")
            } finally {
                isInitializing = false
            }
        }
    }

    suspend fun getRhymes(database: AppDatabase, word: String): Map<String, Int> {
        val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
        if (isWeb) {
            return songwritersnotebook.app.utils.CloudFunctionService.getRhymes(word)
        }

        return withContext(Dispatchers.Default) {
            val queries = database.appDatabaseQueries
            val lookupWord = word.uppercase().trim().filter { it.isLetter() || it == '\'' }
            if (lookupWord.isEmpty()) return@withContext emptyMap()

            val definitions = queries.getWordDefinition(lookupWord).awaitAsList() as List<RhymeWordEntity>
            if (definitions.isEmpty()) return@withContext emptyMap()

            val results = mutableMapOf<String, Int>()

            definitions.forEach { definition ->
                val phonemes = definition.phonemes
                val perfectSuffix = getStressedSuffix(phonemes)
                if (perfectSuffix.isEmpty()) return@forEach

                // The rhyme vowel is the first phoneme of the stressed suffix
                val rhymeVowel = perfectSuffix.split(' ').firstOrNull() ?: return@forEach

                // MUCH FASTER: Uses index on stressedSuffix with prefix search
                val allCandidates = queries.getWordsWithSuffix("$rhymeVowel%").awaitAsList() as List<RhymeWordEntity>

                val targetSyllables = perfectSuffix.count { it.isDigit() }

                allCandidates.forEach { candidate ->
                    if (candidate.word == lookupWord) return@forEach

                    // Check for Perfect Rhyme: Entire stressed suffix matches
                    if (candidate.phonemes.endsWith(perfectSuffix)) {
                        results[candidate.word] = 300 // Perfect
                    } else if (results[candidate.word] == null || results[candidate.word]!! < 300) {
                        // Check for Slant Rhyme: Only the rhyme vowel (first part of suffix) matches
                        // We already know it matches the prefix from the SQL query
                        // Refinement: Must have same syllable count in suffix
                        val candidateSyllables = candidate.stressedSuffix.count { it.isDigit() }
                        if (candidateSyllables == targetSyllables) {
                            // Calculate Slant Similarity Score (200-299)
                            var slantScore = 200
                            val partsA = perfectSuffix.split(' ')
                            val partsB = candidate.stressedSuffix.split(' ')
                            
                            // 1. Reward matching phonemes from the end (weighted higher)
                            val minLen = minOf(partsA.size, partsB.size)
                            for (i in 1..minLen) {
                                if (partsA[partsA.size - i] == partsB[partsB.size - i]) {
                                    slantScore += 15
                                } else break
                            }
                            
                            // 2. Reward matching phonemes from the start (vowel matches + consonants)
                            for (i in 0 until minLen) {
                                if (partsA[i] == partsB[i]) {
                                    slantScore += 5
                                } else break
                            }
                            
                            // Ensure it doesn't exceed 299
                            val finalScore = minOf(slantScore, 299)
                            
                            // Only update if this pronunciation gives a better score
                            if (results[candidate.word] == null || finalScore > (results[candidate.word] ?: 0)) {
                                results[candidate.word] = finalScore
                            }
                        }
                    }
                }
            }
            // Map scores to Boolean for existing API (True = Perfect 300, False = Slant < 300)
            // Wait, RhymeService expects Map<String, Boolean>. We should update RhymeService too 
            // OR change the return type here. Let's change the return type to Map<String, Int> 
            // and update RhymeService to use the actual score.
            results
        }
    }

    suspend fun getRhymesRaw(database: AppDatabase, word: String): Map<String, Int> {
        return getRhymes(database, word)
    }

    private val webCache = mutableMapOf<String, List<String>>()
    private val fetchMutex = kotlinx.coroutines.sync.Mutex()

    private suspend fun getPronunciationsFromFirebase(words: List<String>): Map<String, List<String>> {
        fetchMutex.withLock {
            val uncachedWords = words.map { it.lowercase() }.filter { !webCache.containsKey(it) }
            
            if (uncachedWords.isNotEmpty()) {
                // PRO STABILITY: Wrap the network call in NonCancellable so that if the user 
                // types a new character and cancels the UI job, the in-flight request STILL finishes 
                // and populates the cache. Otherwise, every keystroke re-requests the same 81 words.
                kotlinx.coroutines.withContext(kotlinx.coroutines.NonCancellable) {
                    try {
                        val newPronunciations = songwritersnotebook.app.utils.CloudFunctionService.getPronunciations(uncachedWords)
                        webCache.putAll(newPronunciations)
                        
                        // For any requested words that the server couldn't find, cache them as empty
                        // so we don't keep asking the server for invalid/unknown words.
                        uncachedWords.forEach { word ->
                            if (!webCache.containsKey(word)) {
                                webCache[word] = emptyList()
                            }
                        }
                    } catch (e: Exception) {
                        println("RhymeRepository: Error fetching pronunciations: $e")
                    }
                }
            }
        }
        
        // Return whatever is in the cache for the requested words
        return words.associateWith { word ->
            webCache[word.lowercase()] ?: emptyList()
        }.filter { it.value.isNotEmpty() }
    }

    fun getStressedSuffix(phonemes: String): String {
        val parts = phonemes.trim().split(' ').filter { it.isNotEmpty() }
        var stressIndex = parts.indexOfLast { it.contains('1') }
        if (stressIndex == -1) stressIndex = parts.indexOfLast { it.contains('2') }

        if (stressIndex == -1) return ""
        return parts.subList(stressIndex, parts.size).joinToString(" ")
    }

    suspend fun getPronunciations(database: AppDatabase, words: List<String>): Map<String, List<String>> {
        val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
        if (isWeb) {
            return getPronunciationsFromFirebase(words)
        }

        return withContext(Dispatchers.Default) {
            val queries = database.appDatabaseQueries
            val cleanWords = words.map { 
                it.uppercase().trim().filter { char -> char.isLetter() || char == '\'' }
            }.filter { it.isNotEmpty() }
            if (cleanWords.isEmpty()) return@withContext emptyMap()
            
            val definitions = try {
                queries.getDefinitionsForWords(cleanWords).awaitAsList() as List<RhymeWordEntity>
            } catch (e: Exception) {
                println("RhymeRepository: Error in getDefinitionsForWords (likely schema mismatch): $e")
                emptyList()
            }
            
            val result = mutableMapOf<String, MutableList<String>>()
            definitions.forEach { 
                val word = it.word.lowercase()
                result.getOrPut(word) { mutableListOf() }.add(it.phonemes)
            }
            result
        }
    }
}

// Simple clock helper for commonMain
object ClockTime {
    fun now(): Long = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
}

